import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../../atoms/icon/icon.component';
import { BadgeComponent } from '../../atoms/badge/badge.component';
import { ModalComponent } from '../modal/modal.component';
import { UtilitiesCards } from '../../../shared/interfaces/utilities-cards.interface';
import { Colors, Headlines, Sizes } from '../../../shared/enums/shared.enums';
import BadgeType from '../../../shared/enums/badge-type.enums';
import { TooltipComponent } from '../tooltip/tooltip.component';
import { Tooltip } from '../../../shared/interfaces/tooltip.interface';
import { CaasComponent } from '../../../shared/interfaces/caas/caas.interface';
import { Router } from '@angular/router';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { CapitalizePipe } from '../../capitalize.pipe';

@Component({
  selector: 'hdi-utilities-cards',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    BadgeComponent,
    ModalComponent,
    TooltipComponent,
    RouterModule,
    CapitalizePipe,
  ],
  templateUrl: './utilities-cards.component.html',
  styleUrls: ['./utilities-cards.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class UtilitiesCardsComponent implements OnChanges {
  @Input() utilitiesCards: UtilitiesCards;
  @Input() columns: 'one-column' | 'two-columns' | 'three-columns' =
    'two-columns';
  @Input() headlineSize: Headlines = Headlines.H4;
  @Input() backgroundColor!: 'white' | 'grey';
  @Input() caasData?: CaasComponent;
  @Input() sectionBg?: 'white' | 'grey';

  Sizes = Sizes;
  Colors = Colors;
  BadgeType = BadgeType;
  Headlines = Headlines;

  showModal = false;
  modal: { title: string; description: string };
  tooltip: Tooltip;

  constructor(
    private caasRoutesService: CaasRoutesService,
    private caasBodytextService: CaasBodytextService,
    private router: Router
  ) {
    this.utilitiesCards = {
      title: '',
      key: '',
      items: [],
    };
    this.modal = {
      title: '',
      description: '',
    };
    this.tooltip = {
      id: '',
      description: '',
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      this.utilitiesCards = this.setCaasUtilitiesCards();
      this.backgroundColor = this.sectionBg === 'grey' ? 'grey' : 'white';
      this.columns = this.getCaasColumns();
    }
  }

  openModal(modal: { title: string; description: string }) {
    this.modal = {
      ...modal,
      description: this.caasBodytextService.extractTextEditorContent(
        modal.description
      ),
    };
    this.showModal = !this.showModal;
  }

  openTooltip(tooltip: Tooltip, event: Event) {
    event.stopPropagation();
    this.tooltip = tooltip;
  }

  closeTooltip() {
    this.tooltip = { id: '', description: '' };
  }

  isArray(value: string | string[]): value is string[] {
    return Array.isArray(value);
  }

  getCaasColumns() {
    switch (this.caasData!.formData.st_columns!.value.identifier) {
      case '3':
        return 'three-columns';
      case '2':
        return 'two-columns';
      default:
        return 'two-columns';
    }
  }

  setCaasUtilitiesCards() {
    return {
      title: this.caasData?.formData?.st_header?.value ?? '',
      key: this.caasData?.identifier ?? '',
      items: this.getCaasUtilitiesCardsItems(),
    };
  }

  getCaasUtilitiesCardsItems() {
    if (this.caasData?.formData?.st_items?.value?.length) {
      return this.caasData.formData.st_items.value
        .map((item) => {
          let hasTooltip;
          if (item?.formData?.st_tooltip?.value) {
            hasTooltip = true;
          } else {
            hasTooltip = false;
          }
          const linkType =
            item?.formData?.st_link?.value?.formData?.lt_link_type?.value
              ?.identifier ?? '';
          const itemData = {
            label:
              item?.formData?.st_link?.value?.formData?.lt_link_label?.value ??
              '',
            badgeLabel: item?.formData?.st_badge?.value ?? '',
            tooltip: hasTooltip
              ? {
                  id: item.identifier,
                  description: item?.formData?.st_tooltip?.value ?? '',
                }
              : undefined,
            rightIcon: hasTooltip ? 'circle-info' : undefined,
            target: linkType === 'external' ? '_blank' : '_self',
            url:
              linkType === 'internal'
                ? (this.caasRoutesService.getSeoRouteByIdentifier(
                    item?.formData?.st_link?.value?.formData?.lt_link_internal
                      ?.value?.identifier ?? '',
                    this.router.config
                  )?.path ?? '404')
                : (item?.formData?.st_link?.value?.formData?.lt_link_external
                    ?.value ?? ''),
            title:
              item?.formData?.st_link?.value?.formData?.lt_link_title?.value ??
              '',
          };
          return itemData;
        })
        .filter((item) => item !== undefined);
    } else {
      return [];
    }
  }

  navigateToInternalPage(event: Event, url: string) {
    event.preventDefault();
    this.router.navigate([url]);
  }
}
